import { z } from "zod";
import { violationSchema } from "./call-sequence.js";
// La Home «Oggi» e «Sequenze rispettate oggi» (piano M5, Task 22; D19, questione 267): `GET /api/today`, nel giorno dell'azienda e
// nel perimetro di chi guarda (nella «Guarda come…» quello della persona osservata). Tutti i numeri si leggono dalle colonne
// (`smart_list_entries`, `lead_call_stats`, `calls`, `call_sequences`): nessun giudizio si ricalcola in lettura.
/** Una persona del team, come nelle altre risposte (l'owner di un Lead, il destinatario di una lista). */
const personSchema = z.object({ membershipId: z.string(), displayName: z.string(), active: z.boolean() });
/** Quante sequenze non rispettate la risposta nomina al più, in ordine di tempo; le altre la Home le conta («e altre N»). */
export const HOME_VIOLATIONS_LIMIT = 50;
/**
 * Il filtro di «Chiama i nuovi Lead» (questioni 267 e 342, ruling H13): «Sequenza di chiamate: Lead nuovo, mai chiamato», che il
 * pulsante apre sull'elenco Lead «I miei». Il server conta «Nuovi Lead da contattare» con questo filtro compilato come lo compila
 * l'elenco, così il numero e l'elenco non possono dire due cose diverse.
 */
export const HOME_NEW_LEADS_FILTER = {
    version: 1,
    combinator: "all",
    conditions: [{ kind: "choice", field: "sequence", op: "in", values: ["new_never_called"] }],
    groups: [],
};
/** Quante disco e demo di oggi la risposta porta al più, dalla prima in ordine di ora. */
export const HOME_APPOINTMENTS_LIMIT = 50;
/**
 * A che punto è una disco o una demo di oggi, letto dal server all'istante della richiesta (TASK-11): da fare (comincia più tardi),
 * in corso (dall'ora d'inizio alla fine della durata), da chiudere (finita e ancora aperta: si segna dalla scheda), fatta, il Lead non
 * si è presentato. Le annullate non ci sono.
 */
export const HOME_APPOINTMENT_STATES = ["to_do", "in_progress", "to_close", "held", "no_show"];
/** Una disco o una demo di oggi della persona, con il Lead da aprire e il link Meet quando Google Calendar l'ha creato. */
export const homeAppointmentSchema = z.object({
    id: z.string(),
    kind: z.enum(["disco", "demo"]),
    startsAt: z.string(),
    minutes: z.number().int(),
    state: z.enum(HOME_APPOINTMENT_STATES),
    meetUrl: z.string().nullable(),
    lead: z.object({ id: z.string(), displayName: z.string().nullable(), salonName: z.string().nullable() }),
});
export const homeTodayResponseSchema = z.object({
    /** Il giorno dell'azienda (`AAAA-MM-GG`, nel suo fuso). */
    day: z.string(),
    /**
     * I Lead di cui la persona è owner che l'elenco Lead dà per «Lead nuovo, mai chiamato» (`HOME_NEW_LEADS_FILTER`) e che hanno
     * un'entrata aperta in una Smart List che chi guarda vede; 0 senza «Oggi».
     */
    newLeadsToContact: z.number().int(),
    /** Le chiamate in uscita valide di oggi di cui è autore la persona, con quante hanno «Risposta» e quanti Lead diversi; 0 senza «Oggi». */
    attempts: z.object({ calls: z.number().int(), answered: z.number().int(), leads: z.number().int(), closedEarly: z.number().int().nonnegative().optional() }),
    /**
     * Le disco e le demo di oggi assegnate alla persona (TASK-4, come «Le cose da fare»), non annullate, sui Lead e nei Task che chi
     * guarda vede, in ordine di ora, al più `HOME_APPOINTMENTS_LIMIT`. `null` senza «Oggi» o senza la lettura dei Task.
     */
    appointments: z.array(homeAppointmentSchema).nullable(),
    sequences: z.object({
        respected: z.number().int(),
        total: z.number().int(),
        /** Le non rispettate con il primo motivo, al più `HOME_VIOLATIONS_LIMIT`, dalla prima in ordine di tempo. */
        violations: z.array(z.object({ lead: z.object({ id: z.string(), displayName: z.string().nullable() }), reason: violationSchema })),
        /** Persona per persona (l'owner del Lead), soltanto con `reports.view: all`; altrimenti `null`. */
        byPerson: z.array(z.object({ person: personSchema, respected: z.number().int(), total: z.number().int() })).nullable(),
        /**
         * La regola dell'azienda per la spiegazione sotto il riquadro (ruling H7): i minuti della prima chiamata, le chiamate del primo
         * tentativo, il minimo degli altri (`null` con un tentativo solo) e il venerdì con la sola mattina.
         */
        rule: z.object({
            firstCallMinutes: z.number().int(),
            firstAttemptCalls: z.number().int(),
            otherAttemptsMinCalls: z.number().int().nullable(),
            fridayOne: z.boolean(),
        }),
    }),
    /** Falso per chi vede i numeri di tutti come Admin o Super admin, e nel contesto di piattaforma (decisione del 25 settembre 2026). */
    showToday: z.boolean(),
});
/**
 * Il numero accanto a «Lead» nel menu (piano M6 D24): `GET /api/today/new-leads`, lo stesso «Nuovi Lead da contattare» della Home,
 * letto da solo e senza i report (sono i Lead della persona). 0 per chi non ha «Oggi» e nel contesto di piattaforma fuori dalla vista.
 */
export const homeNewLeadsResponseSchema = z.object({ count: z.number().int() });
//# sourceMappingURL=home.js.map